import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import type { Permission, FamilyRole } from '../../utils/accessControl';

interface PermissionGateProps {
  /**
   * Wymagane uprawnienie (opcjonalne, jeśli podano minimumRole)
   */
  permission?: Permission;
  
  /**
   * Minimalna wymagana rola (opcjonalne, jeśli podano permission)
   */
  minimumRole?: FamilyRole;
  
  /**
   * Zawartość do renderowania, jeśli użytkownik ma uprawnienia
   */
  children: React.ReactNode;
  
  /**
   * Zawartość do renderowania, jeśli użytkownik nie ma uprawnień (opcjonalne)
   */
  fallback?: React.ReactNode;
  
  /**
   * Zawartość do renderowania podczas ładowania (opcjonalne)
   */
  loadingComponent?: React.ReactNode;
}

/**
 * Komponent, który renderuje zawartość tylko dla użytkowników z odpowiednimi uprawnieniami
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  minimumRole,
  children,
  fallback = null,
  loadingComponent = <div>Sprawdzanie uprawnień...</div>,
}) => {
  const { checkPermission, checkMinimumRole, loading } = usePermissions();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (permission) {
        const result = await checkPermission(permission);
        setHasAccess(result);
      } else if (minimumRole) {
        const result = await checkMinimumRole(minimumRole);
        setHasAccess(result);
      } else {
        // Jeśli nie podano ani permission, ani minimumRole, domyślnie zezwalaj
        setHasAccess(true);
      }
    };

    if (!loading) {
      checkAccess();
    }
  }, [permission, minimumRole, checkPermission, checkMinimumRole, loading]);

  if (loading || hasAccess === null) {
    return <>{loadingComponent}</>;
  }

  return <>{hasAccess ? children : fallback}</>;
};

/**
 * Komponent, który renderuje zawartość tylko dla rodziców (parent_a lub parent_b)
 */
export const ParentsOnly: React.FC<Omit<PermissionGateProps, 'minimumRole'>> = (props) => {
  return <PermissionGate minimumRole="parent_b" {...props} />;
};

/**
 * Komponent, który renderuje zawartość tylko dla głównego rodzica (parent_a)
 */
export const PrimaryParentOnly: React.FC<Omit<PermissionGateProps, 'minimumRole'>> = (props) => {
  return <PermissionGate minimumRole="parent_a" {...props} />;
};

/**
 * Komponent, który renderuje zawartość tylko dla obserwatorów i rodziców
 */
export const ObserversAndParentsOnly: React.FC<Omit<PermissionGateProps, 'minimumRole'>> = (props) => {
  return <PermissionGate minimumRole="child_viewer" {...props} />;
};